import { TRIAL_DURATION_TYPES, type PlanTrial } from '@weft/types';
import React, { useMemo } from 'react';
import type { Control } from 'react-hook-form';
import { PlanSettingsAutoCompleteField } from './PlanSettingsAutoCompleteField';
import type { PlanSettingsSelectItem } from './PlanSettingsSelect';

type TrialDurationAutoCompleteProps = {
  className?: string;
  control: Control;
  name: string;
  defaultValue?: PlanTrial | undefined;
  disabled?: boolean;
  freeTrial: boolean;
};

export const TrialDurationAutoComplete: React.FC<TrialDurationAutoCompleteProps> = ({ className, control, name, defaultValue, disabled = false, freeTrial }) => {
  const defaultValueAsItem = useMemo(
    () => getTrialDurationValues(freeTrial).find(({ value }) => value?.duration === defaultValue?.duration && value?.type === defaultValue?.type),
    [defaultValue, freeTrial],
  );

  return (
    <PlanSettingsAutoCompleteField 
      control={control}
      name={name}
      className={className}
      values={getTrialDurationValues(freeTrial)}
      defaultItem={defaultValueAsItem ?? getTrialDurationValues(freeTrial)[0]}
      disabled={disabled}
      label='Trial period'
      mapValueChange={inputNumber => ({
        duration: inputNumber,
        type: TRIAL_DURATION_TYPES.months,
        free: freeTrial,
      })}
    />
  );
};

function getTrialDurationValues(freeTrial: boolean): PlanSettingsSelectItem<PlanTrial | undefined>[] {
  const values = [
    {
      label: 'None',
      value: undefined,
    },
  ];
  const valuesWithWeeks = addWeeksToValues(values, freeTrial);

  return addMonthsToValues(valuesWithWeeks, freeTrial);
}

function addWeeksToValues(values: PlanSettingsSelectItem<PlanTrial | undefined>[], freeTrial: boolean): PlanSettingsSelectItem<PlanTrial | undefined>[] {
  const durationWithWeeks = Array.from({ length: 3 }, (_, i) => i + 1).map<PlanSettingsSelectItem<PlanTrial>>((value) => {
    return { 
      label: getWeekLabelByWeekIndex(value),
      value: {
        type: TRIAL_DURATION_TYPES.weeks,
        duration: value,
        free: freeTrial,
      },
    };
  });

  return [...values, ...durationWithWeeks];
}

function addMonthsToValues(values: PlanSettingsSelectItem<PlanTrial | undefined>[], freeTrial: boolean): PlanSettingsSelectItem<PlanTrial | undefined>[] {
  const durationWithMonths = Array.from({ length: 6 }, (_, i) => i + 1).map<PlanSettingsSelectItem<PlanTrial>>((value) => {
    return { 
      label: getMonthLabelByMonthIndex(value),
      value: {
        type: TRIAL_DURATION_TYPES.months,
        duration: value,
        free: freeTrial,
      },
    };
  });

  return [...values, ...durationWithMonths];
}

function getMonthLabelByMonthIndex(monthIndex: number) {
  return monthIndex === 1 ? `${monthIndex} month` : `${monthIndex} months`;
}

function getWeekLabelByWeekIndex(weekIndex: number) {
  return weekIndex === 1 ? `${weekIndex} week` : `${weekIndex} weeks`;
}